<template>
  <div>
    <div
      v-if="!minimal"
      class="relative w-auto"
    >
      <router-link
        v-if="!isActive && currentTeam && !isDemo"
        :to="{ name: ROUTES.UPGRADE }"
        class="w-full"
      >
        <Button
          label="Reactivate your account"
          class="w-full"
          severity="warning"
          outlined
        />
      </router-link>
      <template v-else>
        <div
          ref="buttonWrapperRef"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        >
          <SplitButton
            :label="buttonLabel"
            :disabled="!isAdmin && (syncHappening || !currentTeam || configChanges.length === 0)"
            :model="publishOptions"
            class="ml-2 w-full"
            @click="attemptToPublish"
          />
          <div class="ml-2 mt-1 text-xs opacity-70 dark:text-white">
            {{ buttonSubtext }}
          </div>
        </div>

        <!-- Hover preview -->
        <PublishChangesPreview
          v-if="configChanges.length > 0"
          :show="showPreview"
          :changes="configChanges"
          :button-ref="buttonWrapperRef"
          position="right"
        />
      </template>
    </div>
    <div v-else>
      <ProgressSpinner
        v-if="syncHappening"
        style="width: 20px; height: 20px"
        stroke-width="8"
        fill="transparent"
        animation-duration=".5s"
      />
    </div>

    <PreviewModal
      :visible="previewModalVisible"
      @update:visible="previewModalVisible = $event"
    />
    <PublishErrors
      :visible="publishErrorsVisible"
      :validation-errors="validationErrors"
      @update:visible="publishErrorsVisible = $event"
    />
    <PublishChangesModal
      :visible="publishChangesModalVisible"
      :changes="configChanges"
      :is-publishing="isPublishingChanges"
      @update:visible="publishChangesModalVisible = $event"
      @publish="handlePublishWithMessage"
    />
    <SnapshotCreationModal
      :open="openCreateSnapshotModal"
      @update-visible="openCreateSnapshotModal = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { PublishState } from '@/stores/configStore'
import { useConfigChanges } from '@/composables/useConfigChanges'
import type { ConfigChange } from '@/composables/useConfigChanges'

import { EyeIcon as PreviewIcon } from '@heroicons/vue/24/outline'
import SnapshotCreationModal from '@/components/common/SnapshotCreationModal.vue'
import PublishChangesModal from '@/components/common/PublishChangesModal.vue'
import PublishChangesPreview from '@/components/common/PublishChangesPreview.vue'
import { useIsFetching } from '@tanstack/vue-query'
import { UseTimeAgo } from '@vueuse/components'
import { watchDebounced } from '@vueuse/core'
import { comma } from 'postcss/lib/list'

const props = defineProps({ minimal: { type: Boolean, default: false } })

const authStore = useAuthStore()
const { user, isAdmin } = storeToRefs(authStore)

const configStore = useConfigStore()
const { publish } = configStore
const { currentTeam, config, fullResponse } = storeToRefs(configStore)
const { isUpdatingConfig, publishState, isDirty } = storeToRefs(configStore)

const subscriptionStore = useSubscriptionStore()
const { isActive } = storeToRefs(subscriptionStore)

const isDemo = inject('isDemo', false)

const { analyzeChanges } = useConfigChanges()

let timeAgo = null
watch(
  currentTeam,
  (team) => {
    if (!team) return
    timeAgo = useTimeAgo(team.last_published_at, {
      showSecond: true,
      updateInterval: 1000
    })
  },
  { immediate: true }
)

const isFetching = useIsFetching()

const validationErrors = ref([])
const openCreateSnapshotModal = ref(false)
const publishOptions = ref([
  {
    label: 'Preview on your website',
    icon: 'pi pi-eye',
    command: () => {
      previewModalVisible.value = true
    }
  }
  // {
  //   label: 'Save new snapshot',
  //   icon: 'pi pi-save',
  //   command: async () => {
  //     openCreateSnapshotModal.value = true
  //   }
  // },
  // { label: 'Load existing snapshot', icon: 'pi pi-folder' }
])

const previewModalVisible = ref(false)
const publishErrorsVisible = ref(false)
const publishChangesModalVisible = ref(false)
const configChanges = ref<ConfigChange[]>([])
const isPublishingChanges = ref(false)
const showPreview = ref(false)
const buttonWrapperRef = ref<HTMLElement | null>(null)

// Analyze changes between draft and published config
const analyzeConfigChanges = async () => {
  if (!config.value || !fullResponse.value) {
    configChanges.value = []
    return
  }

  const publishedConfig = fullResponse.value.stable || null
  configChanges.value = await analyzeChanges(config.value, publishedConfig)
}

// Watch for config changes with debouncing to avoid excessive analysis during rapid edits
watchDebounced(
  [config, fullResponse],
  () => {
    analyzeConfigChanges()
  },
  { debounce: 1000, deep: true, immediate: true }
)

const availableToPublish = computed(() => {
  // return store.state.config.status === 'saved'
  return true
})

const syncHappening = computed(() => {
  return isFetching.value > 0 || isUpdatingConfig.value
})

const buttonLabel = computed(() => {
  const count = configChanges.value.length
  if (count === 0) {
    return 'Fully synced'
  }

  return `Publish ${count} ${count === 1 ? 'change' : 'changes'}`
})

const buttonSubtext = computed(() => {
  const count = configChanges.value.length
  if (count === 0) {
    return 'All changes published to your website'
  }

  return 'Unpublished changes are not live'
})

const attemptToPublish = async () => {
  if (isDemo.value) {
    alert(`Drats! Publishing isn't available within the demo sandbox.`)
    return
  }
  const errors = configStore.validateForPublish()

  if (errors.length > 0) {
    validationErrors.value = errors
    publishErrorsVisible.value = true
  } else {
    // Analyze changes and show modal
    await analyzeConfigChanges()
    publishChangesModalVisible.value = true
  }
}

const handlePublishWithMessage = async (message: string) => {
  isPublishingChanges.value = true
  try {
    await publish(message)
    // Wait a brief moment for the refetch to complete
    await new Promise(resolve => setTimeout(resolve, 500))
    publishChangesModalVisible.value = false
  } finally {
    isPublishingChanges.value = false
  }
}

const handleMouseEnter = () => {
  if (configChanges.value.length > 0) {
    showPreview.value = true
  }
}

const handleMouseLeave = () => {
  showPreview.value = false
}

const buttonText = computed(() => {
  switch (publishState.value) {
    case PublishState.Loading:
      return 'Loading'
    case PublishState.Published:
      return 'Published'
    case PublishState.Dirty:
    case PublishState.Saved:
      return 'Publish To Site'
    default:
      return 'Unknown'
  }
})
</script>

<!-- <script>
import PrePublishIntegrate from '@/components/prepublish/PrePublishIntegrate.vue'
import Button from 'primevue/button'

export default {
  components: {
    PrePublishIntegrate,
    Button
  },

  data() {
    return {
      modalScreen: null
    }
  },

  computed: {
    availableToPublish() {
      if (process.env.NODE_ENV === 'development') return true
      return this.$store.state.config.status === 'saved'
    },

    buttonText() {
      if (process.env.NODE_ENV === 'development') return 'Publish (local)'
      switch (this.$store.state.config.status) {
        case 'loading':
          return 'Loading'

        case 'published':
          return 'Published'

        case 'dirty':
        case 'saving':
          return 'Saving...'

        case 'publishing':
          return 'Publishing...'

        // The only one that enables the button to work
        case 'saved':
        default:
          return 'Publish to site'
      }
    }
  },

  methods: {
    publishIfPossible() {
      if (
        this.$store.getters['config/draftHasLogicWhere'](
          (logic) => logic.$source === 'espplaceholder'
        )
      ) {
        if (this.$store.state.integrations.isLoading) {
          return // TODO don't be lazy and buggy
        }

        this.modalScreen = 'integrate'
        return
      }

      //                // Nope. We don't worry about snippet til after doing the actual publish.
      //                if (!this.$store.getters['gettingStarted/done_snippet']) {
      //                    this.modalScreen = 'snippet';
      //                    return;
      //                }

      this.modalScreen = null
      this.definitelyPublish()
    },

    definitelyPublish() {
      //                alert('Would publish now.');return;
      this.$emit('before')
      this.$store.dispatch('config/PUBLISH').then(
        (response) => this.$emit('success', response),
        (error) => this.$emit('failure', error)
      )
    }
  }
}
</script>

<style type="less"></style> -->
